import { useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import styled, { css } from 'styled-components';

import { InstantShipIcon } from 'shared/icons';
import { Button } from 'shared/components/Button';
import { FeatureFlags } from 'featureFlags/constants/featureFlags';
import { IBuyBarProductVariant } from 'shared/types/BuyBarProductVariant';
import { LocalizedCurrency } from 'shared/components/LocalizedCurrency';
import { logAddToCart } from 'tracking/utils/eCommerceEvents';
import { variableFontRegular } from 'styles/theme';
import { sendBuyBarTrackingEvent } from 'productTemplate/utils/sendBuyBarTrackingEvent';
import { useAuthState } from 'auth/hooks/useAuth';
import { useBuyBarVariants } from 'productTemplate/hooks/useBuyBarVariants';
import { useConstructorClient, useFeatureFlag } from 'shared/hooks';
import { useLogInBeforeCheckout } from 'productTemplate/hooks/useLogInBeforeCheckout';
import { useProductTemplate } from 'productTemplate/hooks/useProductTemplate';
import { useSearchTermState } from 'search/hooks/useSearchTerm';
import { useTranslation } from 'react-i18next';
import { fireSnapchatAddCart } from 'tracking/utils/snapchatEvents';
import { useCurrency } from 'shared/hooks/useCurrency';
import { fireTiktokCheckoutAddCart } from 'tracking/utils/tiktokEvents';
import { checkGuestCohort } from 'checkout/utils/checkGuestCohort';

const InstantShipModal = dynamic(
  () => import('productTemplate/components/modals/InstantShipModal'),
  { ssr: false },
);

export interface IWindowItemProductVariantRowProps {
  condition: string;
  description: string;
  isBestPrice?: boolean;
  isInstant?: boolean;
  isUnderRetail: boolean;
  longestPriceLength: number;
  variant: IBuyBarProductVariant;
}

export const WindowItemProductVariantRow = ({
  condition,
  description,
  isBestPrice = false,
  isUnderRetail,
  isInstant = false,
  longestPriceLength,
  variant,
}: IWindowItemProductVariantRowProps) => {
  const [isHoveringRow, setIsHoveringRow] = useState<boolean>(false);
  const [isHoveringInstant, setIsHoveringInstant] = useState<boolean>(false);
  const [isInstantShipModalOpen, setIsInstantShipModalOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const { currentSearchTerm } = useSearchTermState();
  const {
    id,
    slug,
    localizedSpecialDisplayPriceCents,
    productType,
    name,
    brandName,
    sku,
    isFashionProduct,
  } = useProductTemplate();
  const { mapSizeToVariants } = useBuyBarVariants();
  const { isAuthenticated, user } = useAuthState();
  const { logInBeforeCheckout } = useLogInBeforeCheckout();
  const ConstructorClient = useConstructorClient();
  const retailPrice = localizedSpecialDisplayPriceCents?.amount;
  const productPrice = isInstant
    ? variant?.instantShipLowestPriceCents?.amount
    : variant?.lowestPriceCents?.amount;
  const variants = mapSizeToVariants.get(variant?.sizeOption?.value);
  const isSnapchatEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_SNAPCHAT);
  const isTiktokEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_TIKTOK);
  const isGuestCheckoutEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_GUEST_CHECKOUT);
  const { selectedCurrencyCode } = useCurrency();
  const isInGuestCohort = checkGuestCohort();

  let checkoutLink = [
    `/checkout?productTemplateId=${id}`,
    `expectedPriceCents=${productPrice}`,
    `instantShip=${String(isInstant)}`,
    `shoeCondition=${variant?.shoeCondition}`,
    `size=${variant?.sizeOption?.value}`,
  ].join('&');

  if (productType === 'sneakers') {
    checkoutLink = `${checkoutLink}&boxCondition=${variant.boxCondition}`;
  }

  const handleClick = (e) => {
    if (
      (!isAuthenticated && !isGuestCheckoutEnabled) ||
      (!isAuthenticated && isGuestCheckoutEnabled && !isInGuestCohort)
    ) {
      e.preventDefault();
      logInBeforeCheckout(checkoutLink);
    }
    sendBuyBarTrackingEvent({
      eventName: 'CART_ADD',
      productVariants: [...variants.values()],
      retailPrice: localizedSpecialDisplayPriceCents,
      slug,
      section: '',
      size: variant.sizeOption.value,
      isUnderRetail,
      isApparelProduct: isFashionProduct,
    });

    sendBuyBarTrackingEvent({
      eventName: 'PDP_PRICE_SIZE_SELECTION_TAP',
      productVariants: [...variants.values()],
      retailPrice: localizedSpecialDisplayPriceCents,
      slug,
      section: '',
      size: variant.sizeOption.value,
    });

    logAddToCart({
      priceCents: productPrice,
      condition: variant.boxCondition,
      name,
      brandName,
      sku,
    });

    if (isSnapchatEnabled) {
      void fireSnapchatAddCart({
        brandName,
        productPrice,
        sku,
      });
    }

    if (isTiktokEnabled) {
      void fireTiktokCheckoutAddCart(
        {
          contentCategory: brandName,
          contentId: sku,
          currency: selectedCurrencyCode,
          price: productPrice,
          quantity: 1,
        },
        user?.email,
      );
    }

    ConstructorClient?.tracker?.trackConversion(currentSearchTerm, {
      item_id: id.toString(),
      revenue: productPrice,
    });
  };

  const handleInstantMouseLeave = () => {
    setIsHoveringInstant(false);
  };

  const handleInstantMouseOver = () => {
    setIsHoveringInstant(true);
  };

  const handleInstantShipClick = () => {
    setIsInstantShipModalOpen(true);
  };

  const handleInstantShipClose = () => {
    setIsInstantShipModalOpen(false);
  };

  const handleRowMouseLeave = () => {
    setIsHoveringRow(false);
  };

  const handleRowMouseOver = () => {
    setIsHoveringRow(true);
  };

  return (
    <>
      <WindowItemProductVariantRow.Wrapper
        onMouseLeave={handleRowMouseLeave}
        onMouseOver={handleRowMouseOver}
      >
        <Link href={checkoutLink} passHref prefetch={false}>
          <WindowItemProductVariantRow.Link data-qa="product_variant_link" onClick={handleClick}>
            <WindowItemProductVariantRow.Price
              price={productPrice}
              $showUnderline={isHoveringRow}
              $longestPriceLength={longestPriceLength}
            />
            {isUnderRetail && (
              <WindowItemProductVariantRow.RetailPrice
                $isBestPrice={isBestPrice}
                price={retailPrice}
                $longestPriceLength={longestPriceLength}
              />
            )}
            <WindowItemProductVariantRow.Condition
              $isInstant={isInstant}
              $showUnderline={isHoveringRow}
            >
              {isInstant ? <InstantShipIcon /> : condition}
            </WindowItemProductVariantRow.Condition>
          </WindowItemProductVariantRow.Link>
        </Link>
        {isInstant ? (
          <WindowItemProductVariantRow.Instant
            data-qa="open_instant_ship_window_button"
            $isHoveringInstant={isHoveringInstant}
            $isInstant={isInstant}
            onClick={handleInstantShipClick}
            onMouseLeave={handleInstantMouseLeave}
            onMouseOver={handleInstantMouseOver}
            type="button"
          >
            {isHoveringRow && description}
          </WindowItemProductVariantRow.Instant>
        ) : (
          <WindowItemProductVariantRow.Description>
            {isHoveringRow && description}
          </WindowItemProductVariantRow.Description>
        )}

        <Link href={checkoutLink} passHref>
          <WindowItemProductVariantRow.Link
            data-qa="product_variant_arrow_link"
            onClick={handleClick}
          >
            <WindowItemProductVariantRow.ArrowContainer>
              {isHoveringRow && (
                <WindowItemProductVariantRow.SelectButton data-qa="product_variant_select" inverted>
                  {t('productTemplate.select', 'Select')}
                </WindowItemProductVariantRow.SelectButton>
              )}
            </WindowItemProductVariantRow.ArrowContainer>
          </WindowItemProductVariantRow.Link>
        </Link>
      </WindowItemProductVariantRow.Wrapper>
      {isInstant && isInstantShipModalOpen && (
        <InstantShipModal productTemplateId={id} onClose={handleInstantShipClose} />
      )}
    </>
  );
};

const calculatePriceWidth = (length) => {
  if (length > 6) {
    return '65px';
  }

  if (length > 5) {
    return '60px';
  }

  if (length > 4) {
    return '55px';
  }

  return '40px';
};

WindowItemProductVariantRow.Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  width: 100%;
  position: relative;
  align-items: flex-start;
  height: 21px;
  margin-bottom: 2px;
`;

WindowItemProductVariantRow.Link = styled.a`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
`;

WindowItemProductVariantRow.Price = styled(LocalizedCurrency)<{
  $showUnderline: boolean;
  $longestPriceLength: number;
}>`
  margin-right: 8px;
  width: ${({ $longestPriceLength }) => calculatePriceWidth($longestPriceLength)};
  text-decoration: ${({ $showUnderline }) => $showUnderline && 'underline'};
`;

WindowItemProductVariantRow.RetailPrice = styled(LocalizedCurrency)<{
  $isBestPrice: boolean;
  $longestPriceLength: number;
}>`
  margin-right: 8px;
  width: ${({ $longestPriceLength }) => calculatePriceWidth($longestPriceLength)};
  text-decoration: line-through;
  ${({ $isBestPrice }) =>
    !$isBestPrice &&
    css`
      visibility: hidden;
    `}
`;

WindowItemProductVariantRow.Condition = styled.span<{
  $isInstant: boolean;
  $showUnderline: boolean;
}>`
  margin-right: 8px;
  width: 80px;
  text-decoration: ${({ $showUnderline }) => $showUnderline && 'underline'};

  ${({ $isInstant }) =>
    $isInstant &&
    css`
      font-style: italic;
    `}
`;

WindowItemProductVariantRow.Instant = styled.button<{
  $isHoveringInstant: boolean;
  $isInstant: boolean;
}>`
  margin-right: 60px;
  padding: 0;
  width: 142px;
  text-align: left;

  ${variableFontRegular({ height: '14px', size: '9.5px' })};

  ${({ $isHoveringInstant, $isInstant }) =>
    $isHoveringInstant &&
    $isInstant &&
    css`
      cursor: pointer;
      text-decoration: underline;
    `}
`;

WindowItemProductVariantRow.Description = styled.span`
  margin-right: 60px;
  width: 142px;
  text-transform: none;

  ${variableFontRegular({ height: '14px', size: '9.5px' })};
`;

WindowItemProductVariantRow.ArrowContainer = styled.span`
  width: 44px;
  position: absolute;
  right: 0;
  bottom: 5px;
`;

WindowItemProductVariantRow.SelectButton = styled(Button)`
  text-transform: capitalize;
  padding: 4px;
  height: inherit;
  ${variableFontRegular({ size: '11.5px', height: '11px' })};
`;
